import type { JSONSchemaProperty, ToolHandler } from 'app-components/flux/ai-chat/tools/llmToolsTypes';
import type { ToolDefinition } from 'app-components/flux/ai-chat/aiChatTypes';
import { safeParse } from 'app-components/flux/ai-chat/aiChatUtils';
import { getLogger } from 'app-components/utils/core';
import { returnToolResponse } from 'app-components/flux/ai-chat/tools/llmToolsFramework';
import { executeNavigationIntent } from 'navigation/intents/executor';
import { getAvailableNavigationIntents } from 'navigation/intents/registry';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';
import type { NavigationState } from 'navigation/intents/types';
import 'navigation/intents/registrations';
const log = getLogger('llmWebTools');

// Optional builder args if you want to vary tools by app state later
interface BuildToolsOptions {
  // Example: feature flags, user role, etc.
  enableSpendingTools?: boolean;
}

type Intent = ReturnType<typeof getAvailableNavigationIntents>[number];
type IntentParam = NonNullable<Intent['params']>['definitions'][number];

const buildParamSchema = (definition: IntentParam): JSONSchemaProperty => {
  if (definition.type === 'string[]') {
    const items: JSONSchemaProperty = { type: 'string' };
    if (definition.enum?.length) {
      items.enum = definition.enum;
    }
    return {
      type: 'array',
      items,
      description: definition.description,
    };
  }

  const schema: JSONSchemaProperty = {
    type: definition.type,
    description: definition.description,
  };
  if (definition.enum?.length) {
    schema.enum = definition.enum;
  }
  return schema;
};

const formatIntentSummary = (intent: Intent): string => {
  const definitions = intent.params?.definitions ?? [];
  if (definitions.length === 0) {
    return `- "${intent.name}": ${intent.description}`;
  }

  const paramsSummary = definitions
    .map((definition) => {
      const enumHint = definition.enum?.length ? ` options: ${definition.enum.join(', ')}` : '';
      const requiredHint = definition.required ? ' required' : '';
      return `${definition.name} (${definition.type}${requiredHint})${enumHint}: ${definition.description}`;
    })
    .join('; ');

  return `- "${intent.name}": ${intent.description} [params: ${paramsSummary}]`;
};

const buildNavigateDescription = (intents: Intent[]): string => {
  const lines = [
    'Use this tool ONLY for in-app navigation within Quicken Simplifi.',
    'If the user asks to navigate to an external website or a feature not listed below, do NOT call this tool; respond that you can only navigate within the app.',
    'Do NOT use the "transactions" intent as a fallback. Only use it when the user explicitly asks to view or filter transactions.',
    'Provide params only when the selected intent supports them.',
    'Available intents:',
    ...intents.map(formatIntentSummary),
  ];
  return lines.join('\n');
};

const buildParamsSchema = (intents: Intent[]): JSONSchemaProperty => {
  // Flatten all param definitions across intents into a single permissive params object.
  // Intent-specific validation happens in the intent resolver at runtime.
  const definitions = intents.flatMap((intent) => intent.params?.definitions ?? []);

  const properties: Record<string, JSONSchemaProperty> = {};
  for (const definition of definitions) {
    // If multiple intents declare the same param name, keep the first schema encountered.
    if (properties[definition.name]) continue;
    properties[definition.name] = buildParamSchema(definition);
  }

  return {
    type: 'object',
    description: 'Optional parameters for the intent. Available params depend on the intent being navigated to.',
    additionalProperties: false,
    properties,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function buildWebTools(opts: BuildToolsOptions = {}): ToolDefinition[] {
  const tools: ToolDefinition[] = [];

  // this defaults to true, but shows how you can use opts to control various tool definitions
  if (opts.enableSpendingTools ?? true) {
    log.debug('buildWebTools: enabling web tools with opts', opts);

    /*--------------------------------
     * NAVIGATE
     *------------------------------ */
    const intents = getAvailableNavigationIntents(store?.getState() as RootState | undefined);
    const intentNames = intents.map((intent) => intent.name);

    tools.push({
      name: 'web_navigate',
      description: `Navigate the user to a specific feature in the Quicken Simplifi web app. When the user asks to go somewhere or see something, navigate immediately without asking for confirmation.

Some features support optional parameters for filtering/customization. Pass them in the 'params' object.

After a successful navigation, the returned result may include canonical metadata in \`result.payload.state\`. For example, when opening a saved/custom report by name, \`result.payload.state.resolvedSavedReport.name\` is the actual saved report title that was opened — use that in your user-facing confirmation message.

Available features:${buildNavigateDescription(intents)}`,
      parameters: {
        type: 'object',
        properties: {
          intent: {
            type: 'string',
            enum: intentNames,
            description: 'The navigation intent to execute.',
          },
          params: buildParamsSchema(intents),
        },
        required: ['intent'],
        additionalProperties: false,
      },
    });
  }
  return tools;
}

export const webNavigateTool: ToolHandler = async (msg) => {
  const fnArgs = safeParse(msg.arguments) || {};
  const intent =
    typeof fnArgs.intent === 'string' ? fnArgs.intent : typeof fnArgs.feature === 'string' ? fnArgs.feature : '';
  const args = isRecord(fnArgs.params) ? fnArgs.params : isRecord(fnArgs.args) ? fnArgs.args : undefined;

  log.debug('web_navigate called', { intent, args });

  if (!intent) {
    return returnToolResponse(msg, { success: false, error: 'Missing navigation intent.' });
  }

  const result = await executeNavigationIntent(intent, args);
  if (result.type === 'error') {
    return returnToolResponse(msg, { success: false, error: result.reason });
  }

  const state: NavigationState | undefined = result.type === 'navigate' ? result.payload.state : undefined;
  const resolvedSavedReport = state?.kind === 'saved_report' ? state.resolvedSavedReport : undefined;

  return returnToolResponse(msg, {
    success: true,
    result,
    resolvedSavedReport,
  });
};

// Registry of tool-name → handler
export function buildWebToolHandlers(_opts: BuildToolsOptions = {}): Record<string, ToolHandler> {
  return {
    web_navigate: webNavigateTool,
  };
}
